import { OnboardingState, OnboardingStatus } from '@/types/onboarding';
import { supabase } from '@/integrations/supabase/client';
import { n8nProxyService } from './n8nProxyService';
import { n8nWorkflowUpdateService } from './n8nWorkflowUpdateService';

// Interface para as credenciais da Evolution API
export interface EvolutionApiCredentials {
  credential_id: string;
  credential_name: string;
  instance_name: string;
  updated_at?: string;
}

/**
 * Service responsible for managing the onboarding process
 */
export class OnboardingService {
  private INTEGRATION_TYPE = 'onboarding';
  private TABLE = 'integration_config';
  private API_CACHE: Record<string, { data: any, timestamp: number }> = {};
  private CACHE_DURATION = 5 * 60 * 1000; // 5 minutos

  /**
   * Get the onboarding state for a user
   * @param userId User ID to get onboarding state for
   * @returns OnboardingState if found, null otherwise
   */
  async getOnboardingState(userId: string): Promise<OnboardingState | null> {
    try {
      console.log("🔍 Buscando estado de onboarding para o usuário:", userId);
      
      // Check cache first
      const cacheKey = `onboarding_state_${userId}`;
      const cachedData = this.API_CACHE[cacheKey];
      const now = Date.now();
      
      if (cachedData && (now - cachedData.timestamp) < this.CACHE_DURATION) {
        console.log("✅ Usando dados em cache do onboarding");
        return cachedData.data;
      }
      
      // Query Supabase for the onboarding configuration
      const { data, error } = await supabase
        .from('integration_config')
        .select('*')
        .eq('user_id', userId)
        .eq('type', this.INTEGRATION_TYPE)
        .single();

      if (error) {
        console.error('❌ Erro ao buscar estado de onboarding:', error);
        
        // Try to fetch from n8n if available
        try {
          const n8nStatus = await n8nProxyService.testConnection(false, true);
          if (n8nStatus.connected) {
            const n8nData = await this.fetchOnboardingStateFromN8n(userId);
            if (n8nData) return n8nData;
          }
        } catch (n8nError) {
          console.error('Failed to fetch onboarding state from n8n:', n8nError);
        }
        
        return null;
      }

      if (!data) {
        console.log("⚠️ Nenhum estado de onboarding encontrado, usuário novo");
        // Usuário novo, vamos criar um estado de onboarding para iniciar o processo
        return this.initOnboardingState(userId);
      }

      // Map the database record to OnboardingState
      const config = data.config as Record<string, any>;
      
      const onboardingState: OnboardingState = {
        user_id: userId,
        status: config.status || OnboardingStatus.IN_PROGRESS,
        current_step: config.current_step || 0,
        steps_data: config.steps_data || {},
        last_updated: config.last_updated || new Date().toISOString(),
        completed_at: config.completed_at || undefined
      };

      // Update cache
      this.API_CACHE[cacheKey] = {
        data: onboardingState,
        timestamp: Date.now()
      };

      console.log("✅ Estado de onboarding recuperado:", onboardingState);
      return onboardingState;
    } catch (error) {
      console.error('❌ Erro ao buscar estado de onboarding:', error);
      return null;
    }
  }

  /**
   * Attempt to fetch onboarding state from n8n if available
   */
  private async fetchOnboardingStateFromN8n(userId: string): Promise<OnboardingState | null> {
    try {
      // Check if n8n is configured
      const n8nConfig = await n8nProxyService.getN8nConfig();
      if (!n8nConfig.apiUrl || !n8nConfig.apiKey) {
        return null;
      }
      
      // Prepare the API request
      const apiEndpoint = 'onboarding';
      const response = await n8nProxyService.callN8nApi<{ data: any }>(apiEndpoint, {
        method: 'GET',
        queryParams: { userId }
      });
      
      if (response && response.data) {
        const n8nData = response.data;
        return {
          user_id: userId,
          status: n8nData.status || OnboardingStatus.IN_PROGRESS,
          current_step: n8nData.current_step || 0,
          steps_data: n8nData.steps_data || {},
          last_updated: n8nData.last_updated || new Date().toISOString(),
          completed_at: n8nData.completed_at
        };
      }
      
      return null;
    } catch (error) {
      console.error('Error fetching from n8n:', error);
      return null;
    }
  }

  /**
   * Save the onboarding state
   * @param state OnboardingState to save
   * @returns Saved OnboardingState
   */
  async saveOnboardingState(state: OnboardingState): Promise<OnboardingState> {
    if (!state.user_id) {
      throw new Error('User ID is required to save onboarding state');
    }
    
    try {
      // Set last_updated to now if not provided
      if (!state.last_updated) {
        state.last_updated = new Date().toISOString();
      }
      
      // Check if a record already exists
      const { data: existingData } = await supabase
        .from('integration_config')
        .select('id')
        .eq('user_id', state.user_id)
        .eq('type', this.INTEGRATION_TYPE)
        .single();

      // Convert state to config format
      const config = {
        status: state.status,
        current_step: state.current_step,
        steps_data: state.steps_data,
        last_updated: state.last_updated,
        completed_at: state.completed_at
      };

      // Save to database
      let dbError = null;
      if (existingData) {
        // Update existing record
        const existingConfig = typeof existingData.config === 'object' ? existingData.config : {};
        const { error } = await supabase
          .from('integration_config')
          .update({
            config: {
              ...existingConfig,
              ...config
            },
            updated_at: new Date().toISOString()
          })
          .eq('id', existingData.id);

        dbError = error;
      } else {
        // Insert new record
        const { error } = await supabase
          .from('integration_config')
          .insert({
            user_id: state.user_id,
            type: this.INTEGRATION_TYPE,
            config
          });

        dbError = error;
      }

      if (dbError) {
        console.error('Error saving to database, attempting to save to n8n as fallback:', dbError);
      }

      // Also sync with n8n if available
      try {
        const n8nStatus = await n8nProxyService.testConnection(false, true);
        if (n8nStatus.connected) {
          await this.syncStateWithN8n(state);
        }
      } catch (n8nError) {
        console.error('Failed to sync with n8n, but database save succeeded:', n8nError);
      }
      
      // Update cache
      const cacheKey = `onboarding_state_${state.user_id}`;
      this.API_CACHE[cacheKey] = {
        data: state,
        timestamp: Date.now()
      };
      
      return state;
    } catch (error) {
      console.error('Error saving onboarding state:', error);
      throw error;
    }
  }

  /**
   * Synchronize the onboarding state with n8n
   */
  private async syncStateWithN8n(state: OnboardingState): Promise<void> {
    try {
      // Check if n8n is configured first
      const n8nConfig = await n8nProxyService.getN8nConfig();
      if (!n8nConfig.apiUrl || !n8nConfig.apiKey) {
        return;
      }
      
      // Call n8n API to update onboarding state
      const apiEndpoint = 'onboarding';
      await n8nProxyService.callN8nApi(apiEndpoint, {
        method: 'POST',
        body: { 
          userId: state.user_id,
          status: state.status,
          current_step: state.current_step,
          steps_data: state.steps_data,
          last_updated: state.last_updated,
          completed_at: state.completed_at
        }
      });
      
      console.log('Successfully synced onboarding state with n8n');
    } catch (error) {
      console.error('Error syncing with n8n:', error);
      // We don't throw here as this is a secondary operation
    }
  }

  /**
   * Initialize a new onboarding state for a user
   * @param userId User ID to initialize onboarding for
   * @returns New OnboardingState
   */
  async initOnboardingState(userId: string): Promise<OnboardingState> {
    console.log("🆕 Inicializando novo estado de onboarding para o usuário:", userId);
    
    const newState: OnboardingState = {
      user_id: userId,
      status: OnboardingStatus.IN_PROGRESS, // Começamos como IN_PROGRESS para garantir que o usuário seja direcionado
      current_step: 0,
      steps_data: {},
      last_updated: new Date().toISOString()
    };
    
    try {
      const savedState = await this.saveOnboardingState(newState);
      console.log("✅ Novo estado de onboarding criado:", savedState);
      return savedState;
    } catch (error) {
      console.error("❌ Erro ao criar estado de onboarding:", error);
      // Mesmo com erro, retornamos o estado para que o fluxo de onboarding possa continuar
      return newState;
    }
  }

  /**
   * Update a step in the onboarding process
   * @param userId User ID
   * @param stepId Step ID to update
   * @param stepData Data for the step
   * @returns Updated OnboardingState
   */
  async updateStep(
    userId: string, 
    stepId: string, 
    stepData: Record<string, any>
  ): Promise<OnboardingState> {
    const state = await this.getOnboardingState(userId);
    
    if (!state) {
      throw new Error('Onboarding state not found');
    }
    
    const updatedState: OnboardingState = {
      ...state,
      steps_data: {
        ...state.steps_data,
        [stepId]: stepData
      },
      last_updated: new Date().toISOString()
    };
    
    // Also track with n8n analytics if available
    try {
      const n8nStatus = await n8nProxyService.testConnection(false, true);
      if (n8nStatus.connected) {
        // Track progress event
        await n8nProxyService.callN8nApi('events/track', {
          method: 'POST',
          body: {
            event: 'onboarding_step_completed',
            properties: {
              user_id: userId,
              step_id: stepId,
              timestamp: new Date().toISOString()
            }
          }
        });
      }
    } catch (error) {
      console.error('Failed to track step completion in n8n:', error);
    }
    
    return this.saveOnboardingState(updatedState);
  }

  /**
   * Complete the onboarding process for a user
   * @param userId User ID to complete onboarding for
   * @returns Updated OnboardingState
   */
  async completeOnboarding(userId: string): Promise<OnboardingState> {
    try {
      console.log(`Concluindo onboarding para o usuário: ${userId}`);
      
      // Check if the user has an existing onboarding state
      const state = await this.getOnboardingState(userId);
      
      if (!state) {
        throw new Error('Onboarding state not found');
      }
      
      // Marca cada etapa individual como concluída para garantir visualização correta nos cards
      const onboardingSteps = ['welcome', 'whatsapp', 'business_info', 'ai_agent', 'calendar', 'content', 'completion'];
      const updatedStepsData = { ...state.steps_data };
      
      // Para cada etapa de onboarding...
      onboardingSteps.forEach(stepKey => {
        // Se a etapa já tem dados, adiciona o flag completed
        if (updatedStepsData[stepKey]) {
          updatedStepsData[stepKey] = {
            ...updatedStepsData[stepKey],
            completed: true
          };
        } else {
          // Se a etapa não tem dados, cria um objeto básico com completed
          updatedStepsData[stepKey] = { completed: true };
        }
      });
      
      // Update the state with all steps marked as completed
      const updatedState: OnboardingState = {
        ...state,
        status: OnboardingStatus.COMPLETED,
        completed_at: new Date().toISOString(),
        last_updated: new Date().toISOString(),
        steps_data: updatedStepsData
      };

      // Salvar o status de onboarding completo no localStorage para redundância
      try {
        localStorage.setItem('onboarding_completed', 'true');
        localStorage.setItem('onboarding_completed_at', updatedState.completed_at);
        localStorage.setItem('onboarding_user_id', userId);
        console.log('Onboarding status salvo no localStorage');
      } catch (localStorageError) {
        console.error('Erro ao salvar status de onboarding no localStorage:', localStorageError);
      }

      // Track completion with n8n analytics if available
      try {
        const n8nStatus = await n8nProxyService.testConnection(false, true);
        if (n8nStatus.connected) {
          // First, track the completion event
          await n8nProxyService.callN8nApi('events/track', {
            method: 'POST',
            body: {
              event: 'onboarding_completed',
              properties: {
                user_id: userId,
                timestamp: new Date().toISOString()
              }
            }
          });
          
          // Then, update the workflow with all onboarding data
          this.updateWorkflowWithOnboardingData(userId, updatedStepsData)
            .then(result => {
              console.log('Workflow atualizado com dados do onboarding:', result);
            })
            .catch(error => {
              console.error('Erro ao atualizar workflow com dados do onboarding:', error);
            });
        }
      } catch (error) {
        console.error('Failed to track completion in n8n:', error);
      }
      
      // Salve o estado atualizado no serviço principal
      const savedState = await this.saveOnboardingState(updatedState);
      
      // Tente salvar novamente se a verificação falhar
      const verificationState = await this.getOnboardingState(userId);
      if (verificationState?.status !== OnboardingStatus.COMPLETED) {
        console.error('Verificação falhou: o status de onboarding não foi salvo corretamente. Tentando novamente...');
        
        // Tente salvar novamente
        await this.saveOnboardingState(updatedState);
        
        // Verifique uma segunda vez
        const secondVerification = await this.getOnboardingState(userId);
        if (secondVerification?.status !== OnboardingStatus.COMPLETED) {
          console.error('Segunda verificação falhou: status de onboarding ainda não está salvo corretamente');
        } else {
          console.log('Segunda verificação bem-sucedida: status de onboarding salvo corretamente');
        }
      } else {
        console.log('Verificação bem-sucedida: status de onboarding salvo corretamente');
      }
      
      return savedState;
    } catch (error) {
      console.error('Error completing onboarding:', error);
      throw error;
    }
  }

  /**
   * Atualiza um workflow do n8n com os dados do onboarding
   * @param userId ID do usuário
   * @param stepsData Dados do onboarding
   * @returns Resultado da atualização
   */
  async updateWorkflowWithOnboardingData(userId: string, stepsData: Record<string, any>) {
    try {
      console.log('Atualizando workflow com dados do onboarding para o usuário:', userId);
      
      // Obter configuração do n8n para o usuário
      const { data: configData, error: configError } = await supabase
        .from('integration_config')
        .select('config')
        .eq('user_id', userId)
        .eq('type', 'n8n')
        .single();
      
      if (configError) {
        console.error('Erro ao obter configuração do n8n:', configError);
        throw new Error(`Erro ao obter configuração do n8n: ${configError.message}`);
      }
      
      if (!configData || !configData.config) {
        console.error('Configuração do n8n não encontrada para o usuário:', userId);
        throw new Error('Configuração do n8n não encontrada');
      }
      
      const n8nConfig = configData.config as Record<string, any>;
      
      // Verificar se o workflow_id está presente
      if (!n8nConfig.workflow_id) {
        console.error('ID do workflow não encontrado na configuração do n8n');
        throw new Error('ID do workflow não encontrado');
      }
      
      // Atualizar o workflow com os dados do onboarding
      return await n8nWorkflowUpdateService.updateWorkflowWithOnboardingData(
        n8nConfig.workflow_id,
        stepsData,
        userId
      );
    } catch (error) {
      console.error('Erro ao atualizar workflow com dados do onboarding:', error);
      throw error;
    }
  }

  /**
   * Reset the onboarding process
   * @param userId User ID
   * @returns New OnboardingState
   */
  async resetOnboarding(userId: string): Promise<OnboardingState> {
    const state = await this.getOnboardingState(userId);
    
    if (state) {
      // Delete existing record
      await supabase
        .from('integration_config')
        .delete()
        .eq('user_id', userId)
        .eq('type', this.INTEGRATION_TYPE);
    }
    
    return this.initOnboardingState(userId);
  }

  /**
   * Check if a user has completed onboarding
   * @param userId User ID to check
   * @returns Whether the user has completed onboarding
   */
  async hasCompletedOnboarding(userId: string): Promise<boolean> {
    const state = await this.getOnboardingState(userId);
    return state?.status === OnboardingStatus.COMPLETED;
  }

  /**
   * Verificar se o onboarding do usuário está completo
   */
  async isOnboardingComplete(userId: string): Promise<boolean> {
    try {
      console.log("🔍 OnboardingService: Verificando se onboarding está completo para usuário", userId);
      const state = await this.getOnboardingState(userId);
      
      if (!state) {
        console.log("🆕 OnboardingService: Usuário não tem estado de onboarding, criando novo");
        await this.createInitialState(userId);
        return false;
      }
      
      const isComplete = state.status === OnboardingStatus.COMPLETED;
      console.log(`📊 OnboardingService: Status de onboarding para usuário ${userId}: ${isComplete ? 'Completo' : 'Não completo'}`);
      return isComplete;
    } catch (err) {
      console.error("❌ OnboardingService: Erro ao verificar onboarding", err);
      return false; // Em caso de erro, assumimos que não está completo
    }
  }
  
  /**
   * Criar estado inicial de onboarding para um novo usuário
   */
  private async createInitialState(userId: string): Promise<OnboardingState> {
    const initialState: OnboardingState = {
      user_id: userId,
      status: OnboardingStatus.IN_PROGRESS,
      current_step: 0,
      steps_data: {},
      last_updated: new Date().toISOString()
    };
    
    return this.saveOnboardingState(initialState);
  }
  
  /**
   * Atualiza parcialmente o estado de onboarding.
   * Método mais eficiente quando só precisa atualizar alguns campos
   */
  async updateOnboardingState(userId: string, updates: Partial<OnboardingState>): Promise<OnboardingState | null> {
    try {
      console.log("🔄 OnboardingService: Atualizando estado de onboarding para usuário", userId);
      
      // Primeiro, buscar o estado atual
      const currentState = await this.getOnboardingState(userId);
      if (!currentState) {
        console.error("❌ OnboardingService: Estado não encontrado para atualização");
        return null;
      }
      
      // Mesclar as atualizações com o estado atual
      const mergedState: OnboardingState = {
        ...currentState,
        ...updates,
        // Preservar a estrutura steps_data e fazer merge
        steps_data: {
          ...currentState.steps_data,
          ...(updates.steps_data || {})
        },
        last_updated: new Date().toISOString()
      };
      
      // Usar o método existente para salvar
      return await this.saveOnboardingState(mergedState);
    } catch (err) {
      console.error("❌ OnboardingService: Erro ao atualizar estado de onboarding", err);
      return null;
    }
  }

  /**
   * Get Evolution API credentials for a user
   * @param userId User ID
   * @returns Evolution API credentials or null if not found
   */
  async getEvolutionApiCredentials(userId: string): Promise<EvolutionApiCredentials | null> {
    try {
      console.log("🔍 Buscando credenciais da Evolution API para o usuário:", userId);
      
      // Check if we have evolution-api credentials for this user
      const { data, error } = await supabase
        .from('integration_config')
        .select('config')
        .eq('user_id', userId)
        .eq('type', 'evolution-api')
        .single();
      
      if (error) {
        console.error("❌ Erro ao buscar credenciais da Evolution API:", error);
        return null;
      }
      
      if (!data || !data.config) {
        console.log("⚠️ Nenhuma credencial da Evolution API encontrada para o usuário");
        return null;
      }
      
      // Validar se os campos necessários estão presentes
      const config = data.config as Record<string, any>;
      if (!config.credential_id || !config.instance_name) {
        console.log("⚠️ Credencial da Evolution API incompleta");
        return null;
      }
      
      console.log("✅ Credenciais da Evolution API encontradas para instância:", config.instance_name);
      return {
        credential_id: config.credential_id,
        credential_name: config.credential_name || 'Default',
        instance_name: config.instance_name,
        updated_at: config.updated_at
      };
    } catch (error) {
      console.error("❌ Erro ao buscar credenciais da Evolution API:", error);
      return null;
    }
  }

  /**
   * Save Evolution API credentials
   * @param userId User ID
   * @param credentials Evolution API credentials
   * @returns True if operation was successful
   */
  async saveEvolutionApiCredentials(userId: string, credentials: {
    credential_id: string,
    credential_name: string,
    instance_name: string
  }): Promise<boolean> {
    try {
      console.log("💾 Salvando credenciais da Evolution API para o usuário:", userId);
      
      // Check if we already have evolution-api credentials for this user
      const { data: existingData, error: queryError } = await supabase
        .from('integration_config')
        .select('id, config')
        .eq('user_id', userId)
        .eq('type', 'evolution-api')
        .single();
      
      const config = {
        credential_id: credentials.credential_id,
        credential_name: credentials.credential_name,
        instance_name: credentials.instance_name,
        updated_at: new Date().toISOString()
      };
      
      if (queryError && queryError.code !== 'PGRST116') { // PGRST116 is the "not found" error code
        console.error("❌ Erro ao verificar credenciais existentes:", queryError);
        return false;
      }
      
      if (existingData) {
        // Update existing record
        const existingConfig = typeof existingData.config === 'object' && existingData.config ? existingData.config : {};
        const { error } = await supabase
          .from('integration_config')
          .update({
            config: {
              ...existingConfig,
              ...config
            },
            updated_at: new Date().toISOString()
          })
          .eq('id', existingData.id);
        
        if (error) {
          console.error("❌ Erro ao atualizar credenciais da Evolution API:", error);
          return false;
        }
      } else {
        // Insert new record
        const { error } = await supabase
          .from('integration_config')
          .insert({
            user_id: userId,
            type: 'evolution-api',
            config,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          });
        
        if (error) {
          console.error("❌ Erro ao inserir credenciais da Evolution API:", error);
          return false;
        }
      }
      
      console.log("✅ Credenciais da Evolution API salvas com sucesso");
      return true;
    } catch (error) {
      console.error("❌ Erro ao salvar credenciais da Evolution API:", error);
      return false;
    }
  }

  /**
   * Salva o estado de escaneamento do QR code do WhatsApp
   * @param userId ID do usuário
   * @param scanned Boolean indicando se o QR foi escaneado
   * @param instanceName Nome da instância do WhatsApp (opcional)
   * @returns True se salvou com sucesso, False caso contrário
   */
  async saveWhatsAppQrScannedState(userId: string, scanned: boolean, instanceName?: string): Promise<boolean> {
    try {
      console.log(`Salvando estado de escaneamento do QR code: ${scanned}`, { userId, instanceName });
      
      // Obter o estado atual de onboarding
      const currentState = await this.getOnboardingState(userId);
      if (!currentState) {
        console.error("Não foi possível encontrar o estado de onboarding para salvar QR escaneado");
        return false;
      }
      
      // Verificar se o step do WhatsApp já existe nos dados
      const hasWhatsAppData = currentState.steps_data && currentState.steps_data.whatsapp;
      
      // Preparar os dados atualizados
      const updatedWhatsAppData = {
        ...(hasWhatsAppData ? currentState.steps_data.whatsapp : {}),
        qr_code_scanned: scanned
      };
      
      // Se tiver o nome da instância, incluir também
      if (instanceName) {
        updatedWhatsAppData.instance_name = instanceName;
      }
      
      // Atualizar o step específico
      await this.updateStep(userId, 'whatsapp', updatedWhatsAppData);
      
      // Limpar o cache para forçar um recarregamento na próxima vez
      const cacheKey = `onboarding_state_${userId}`;
      delete this.API_CACHE[cacheKey];
      
      return true;
    } catch (error) {
      console.error("Erro ao salvar estado de escaneamento do QR code:", error);
      return false;
    }
  }

  /**
   * Verifica se o QR code do WhatsApp já foi escaneado anteriormente
   * @param userId ID do usuário
   * @returns True se o QR code já foi escaneado, False caso contrário ou em caso de erro
   */
  async hasWhatsAppQrBeenScanned(userId: string): Promise<boolean> {
    try {
      const state = await this.getOnboardingState(userId);
      if (!state || !state.steps_data || !state.steps_data.whatsapp) {
        return false;
      }
      
      return !!state.steps_data.whatsapp.qr_code_scanned;
    } catch (error) {
      console.error("Erro ao verificar se QR code foi escaneado:", error);
      return false;
    }
  }
}

// Export a singleton instance for use throughout the app
export const onboardingService = new OnboardingService(); 